import type { ConfigProps } from '../../configTypes';
import { getContributorPathSegment } from '../lib/contributorPaths';
import { partitionContributorSocialMedia } from '../lib/partitionContributorSocialMedia';
import type {
  ContributorHighlightField,
  ContributorPost,
  ContributorProfileData,
  ContributorSocialMedia,
} from '../types/contributorProfile';
import executeGraphQlRequest from './executeGraphQlRequest';
import transformImage from '../transform/transformImage';
import transformSeo from '../transform/transformSEO';
import type { TransformedSeo } from '../transform/transformSEO';

type RawContributorPost = {
  id: string;
  post_title?: string;
  featured_image?: string;
  post_url?: string;
  contribution_type?: string;
  date?: string;
  post_excerpt?: string;
};

type RawContributorNode = {
  id: string;
  title?: string;
  slug: string;
  uri: string;
  content?: string;
  career?: string;
  terms?: { nodes: { id: string; name: string; slug: string }[] };
  featuredImage?: {
    node: { altText?: string; publicId?: string; sourceUrl?: string };
  };
  socialMedia?: ContributorSocialMedia[];
  highlightFields?: ContributorHighlightField[];
  contributorPosts?: RawContributorPost[];
  seo?: {
    canonicalUrl?: string;
    robots?: string;
    openGraph?: {
      title?: string;
      description?: string;
      locale?: string;
      type?: string;
      updatedTime?: string;
      siteName?: string;
      image?: {
        url?: string;
        width?: number;
        height?: number;
        type?: string;
      };
      twitterMeta?: { card?: string };
    };
    jsonLd?: { raw?: string };
    pageHeadCode?: string;
    pageFooterCode?: string;
  };
};

export const contributorProfileQuery = (): string => `
query ContributorProfile($slug: ID!) {
  contributor(id: $slug, idType: SLUG) {
    id
    title
    slug
    uri
    content
    career
    terms {
      nodes {
        id
        name
        slug
      }
    }
    featuredImage {
      node {
        altText
        publicId
        sourceUrl
      }
    }
    socialMedia {
      key
      value
    }
    highlightFields {
      type
      items
      links
    }
    contributorPosts {
      id
      post_title
      featured_image
      post_url
      contribution_type
      date
      post_excerpt
    }
    seo {
      canonicalUrl
      robots
      openGraph {
        title
        description
        locale
        type
        updatedTime
        siteName
        image {
          url
          width
          height
          type
        }
        twitterMeta {
          card
        }
      }
      jsonLd {
        raw
      }
      pageHeadCode
      pageFooterCode
    }
  }
}
`;

export default async function fetchContributorProfile(
  config: ConfigProps,
  slug: string,
): Promise<ContributorProfileData | null> {
  if (!slug) return null;

  let data: { contributor: RawContributorNode | null } | null = null;
  try {
    data = await executeGraphQlRequest<{
      contributor: RawContributorNode | null;
    }>({
      cmsUrl: config.cmsUrl,
      query: contributorProfileQuery(),
      variables: { slug },
    });
  } catch {
    return null;
  }

  const raw = data?.contributor;
  if (!raw) return null;

  const publicId = raw.featuredImage?.node?.publicId ?? '';
  const srcUrl = raw.featuredImage?.node?.sourceUrl;
  const imageSrc = transformImage(publicId, {}, srcUrl) ?? undefined;

  const termRoles = (raw.terms?.nodes ?? []).map((t) => t.name).filter(Boolean);
  const roles =
    termRoles.length > 0 ? termRoles : raw.career ? [raw.career] : ['Writer'];

  const postDateTimestamp = (date?: string): number => {
    const parsed = Date.parse(date ?? '');
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const posts: ContributorPost[] = [...(raw.contributorPosts ?? [])]
    .sort((a, b) => postDateTimestamp(b.date) - postDateTimestamp(a.date))
    .map((p: RawContributorPost) => ({
      id: p.id,
      title: p.post_title ?? '',
      url: p.post_url ?? '',
      featuredImage: p.featured_image,
      contributionType: p.contribution_type ?? '',
      date: p.date ?? '',
      excerpt: p.post_excerpt,
    }));

  const seo: TransformedSeo | undefined = raw.seo
    ? transformSeo({
        generalSettings: {},
        pageSeo: raw.seo as Parameters<typeof transformSeo>[0]['pageSeo'],
        config,
        uri: `/${getContributorPathSegment(config)}/${slug}`,
      })
    : undefined;

  const { socialMedia, careerDescription, subjectAreas } =
    partitionContributorSocialMedia(raw.socialMedia);

  return {
    id: raw.id,
    name: raw.title ?? '',
    slug: raw.slug,
    uri: raw.uri,
    bio: raw.content,
    career: raw.career,
    careerDescription,
    roles,
    imageSrc,
    imageAlt: raw.featuredImage?.node?.altText ?? raw.title ?? '',
    socialMedia,
    subjectAreas,
    highlightFields: raw.highlightFields,
    posts,
    seo,
  };
}
